import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FlaskConical, Loader2, UploadCloud, CheckCircle2, XCircle, Clock, Link2, Play, Banknote } from "lucide-react";
import { API } from "@/constants/roles";
import { TestPackView } from "@/components/admin/TestPackView";

const STATUS_CHIP = {
  assigned: { label: "Sent — start when ready", cls: "border-sky-500/30 bg-sky-500/15 text-sky-300" },
  in_progress: { label: "In progress", cls: "border-sky-500/30 bg-sky-500/15 text-sky-300" },
  submitted: { label: "Submitted — in review", cls: "border-amber-500/30 bg-amber-500/15 text-amber-300" },
  reviewed: { label: "Reviewed", cls: "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-3)]" },
  approved: { label: "Passed", cls: "border-emerald-500/30 bg-emerald-500/15 text-emerald-300" },
  rejected: { label: "Not approved", cls: "border-rose-500/30 bg-rose-500/15 text-rose-300" },
};

// Applicant-facing paid test card: frozen pack snapshot, agreed payment + deadline,
// start/submit flow, dual-outcome review display (payment eligibility vs hiring result).
export const TestAssignmentCard = () => {
  const [test, setTest] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [file, setFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    const token = localStorage.getItem("melto_token");
    axios.get(`${API}/my/test-assignment`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setTest(r.data); setLoaded(true); })
      .catch((err) => { if (err.response?.status === 404) setLoaded(true); });
  }, []);

  useEffect(load, [load]);

  if (!loaded || !test) return null;

  const amount = test.agreed_payment ?? test.pack_snapshot?.payment_amount ?? 20;
  const overdue = test.deadline_at && ["assigned", "in_progress"].includes(test.status) && test.deadline_at < new Date().toISOString();

  const start = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("melto_token");
      await axios.post(`${API}/my/test-assignment/${test.id}/start`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Test started — good luck! Submit before the deadline.");
      load();
    } catch {
      toast.error("Could not start the test.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file && !externalUrl.trim()) {
      toast.error("Upload a file or paste an external link.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      fd.append("note", note);
      fd.append("external_url", externalUrl.trim());
      const token = localStorage.getItem("melto_token");
      await axios.post(`${API}/my/test-assignment/${test.id}/submit`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Test submitted — the team will review it now.");
      setFile(null);
      setExternalUrl("");
      setNote("");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const chip = STATUS_CHIP[test.status] || STATUS_CHIP.assigned;

  return (
    <div data-testid="test-assignment-card" className="mt-6 rounded-3xl border border-violet-500/25 bg-[var(--pv-card)] p-8 sm:p-10">
      <div className="flex flex-wrap items-center gap-2.5">
        <FlaskConical className="h-5 w-5 text-violet-400" />
        <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Your paid animation test</h2>
        <span data-testid="test-assignment-status" className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${chip.cls}`}>{chip.label}</span>
        <span data-testid="test-assignment-payment" className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold text-amber-300">
          <Banknote className="h-3 w-3" /> agreed ${amount}
        </span>
        {test.deadline_at && ["assigned", "in_progress"].includes(test.status) && (
          <span data-testid="test-assignment-deadline" className={`flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${overdue ? "border-rose-500/30 bg-rose-500/10 text-rose-300" : "border-[var(--pv-border)] text-[var(--pv-text-4)]"}`}>
            <Clock className="h-3 w-3" /> {overdue ? "Overdue — submit ASAP" : `Due ${new Date(test.deadline_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
          </span>
        )}
      </div>

      {test.brief && (
        <div className="mt-4 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Note from the team</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--pv-text-2)]">{test.brief}</p>
        </div>
      )}

      {test.pack_snapshot ? (
        <div className="mt-5">
          <TestPackView pack={test.pack_snapshot} version={test.pack_version} />
        </div>
      ) : (
        <p className="mt-3 text-xs text-[var(--pv-text-4)]">
          Complete the brief above. If your submission is accepted as a completed attempt, you receive the agreed <b className="text-violet-300">${amount} test payment</b>.
        </p>
      )}

      {test.status === "assigned" && (
        <div className="mt-6">
          <button data-testid="test-start-btn" onClick={start} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-violet-500 px-6 py-2.5 text-xs font-bold text-white hover:bg-violet-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Start test
          </button>
          <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Starting marks the test in progress — submit below when your animation is ready.</p>
        </div>
      )}

      {["assigned", "in_progress"].includes(test.status) && (
        <form data-testid="test-submit-form" onSubmit={submit} className="mt-6 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" data-testid="test-file-btn" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-5 py-2.5 text-xs font-bold text-violet-300 hover:bg-violet-500/20">
              <UploadCloud className="h-4 w-4" /> {file ? file.name : "Upload animation file"}
            </button>
            <input ref={fileRef} data-testid="test-file-input" type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <span className="text-[10px] font-bold uppercase text-[var(--pv-text-4)]">or</span>
            <div className="flex min-w-56 flex-1 items-center gap-2 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2">
              <Link2 className="h-3.5 w-3.5 shrink-0 text-[var(--pv-text-4)]" />
              <input data-testid="test-external-url" value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} placeholder="External link (Drive, YouTube unlisted…)" className="w-full bg-transparent text-xs font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:outline-none" />
            </div>
          </div>
          <textarea data-testid="test-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the reviewer (optional)" className="w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-violet-400 focus:outline-none" />
          <button data-testid="test-submit-btn" type="submit" disabled={busy || (!file && !externalUrl.trim())} className="flex items-center gap-1.5 rounded-full bg-violet-500 px-6 py-2.5 text-xs font-bold text-white hover:bg-violet-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />} Submit test
          </button>
        </form>
      )}
      {test.status === "submitted" && (
        <p data-testid="test-submitted-note" className="mt-5 flex items-center gap-2 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-xs font-semibold text-amber-300">
          <Clock className="h-4 w-4" /> Submitted {test.submitted_at ? new Date(test.submitted_at).toLocaleString() : ""} — under review now. The agreed ${amount} is queued once your submission is accepted as a completed attempt.
        </p>
      )}
      {test.status === "approved" && (
        <p data-testid="test-approved-note" className="mt-5 flex items-center gap-2 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-xs font-semibold text-emerald-300">
          <CheckCircle2 className="h-4 w-4" /> Passed — your ${amount} test payment is queued for payout.
        </p>
      )}
      {test.status === "reviewed" && (
        <div data-testid="test-reviewed-note" className={`mt-5 rounded-2xl border px-4 py-3 ${test.result === "passed" ? "border-emerald-500/20 bg-emerald-500/10" : "border-rose-500/20 bg-rose-500/10"}`}>
          <p className={`flex items-center gap-2 text-xs font-bold ${test.result === "passed" ? "text-emerald-300" : "text-rose-300"}`}>
            {test.result === "passed" ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {test.result === "passed" ? "Your test passed" : "Your test didn't pass this time"}
          </p>
          <p className="mt-1.5 text-xs leading-relaxed text-[var(--pv-text-3)]">
            {test.completion_status === "accepted"
              ? `Your submission was accepted as a completed attempt — the agreed $${amount} payment is queued for payout.`
              : "The submission wasn't accepted as a completed attempt of the test brief, so no payment is owed."}
          </p>
        </div>
      )}
      {test.status === "rejected" && (
        <div data-testid="test-rejected-note" className="mt-5 rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3">
          <p className="flex items-center gap-2 text-xs font-bold text-rose-300"><XCircle className="h-4 w-4" /> Not approved this time</p>
          {test.feedback && <p className="mt-1.5 text-xs leading-relaxed text-rose-200/80">{test.feedback}</p>}
        </div>
      )}
    </div>
  );
};
